import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

/**
 * Hook para gestionar la conexión en tiempo real vía SignalR con la sala del ticket,
 * reconciliar mensajes optimistas y sincronizar eventos de actividad.
 */
export const useTicketSignalR = ({
  activeTicketId,
  token,
  setMessages,
  markCurrentAsRead,
  fetchTicketDetail,
  onTicketUpdated,
  onRefreshHistory,
  ticketDetail,
  propTicket
}) => {
  const hubConnectionRef = useRef(null);
  const fetchTicketDetailRef = useRef(fetchTicketDetail);
  fetchTicketDetailRef.current = fetchTicketDetail;

  const onTicketUpdatedRef = useRef(onTicketUpdated);
  onTicketUpdatedRef.current = onTicketUpdated;

  const onRefreshHistoryRef = useRef(onRefreshHistory);
  onRefreshHistoryRef.current = onRefreshHistory;

  const ticketDetailRef = useRef(ticketDetail);
  ticketDetailRef.current = ticketDetail;

  const markCurrentAsReadRef = useRef(markCurrentAsRead);
  markCurrentAsReadRef.current = markCurrentAsRead;

  useEffect(() => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    let isCancelled = false;
    let connection = null;

    const startSignalR = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (!resp.ok) {
          console.error(`[TicketDetailView] Error al consultar /api/SupportEngine/GetBaseUrl: HTTP ${resp.status}`);
          return;
        }
        const { baseUrl } = await resp.json();
        if (isCancelled) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        hubConnectionRef.current = connection;

        connection.onreconnected(() => {
          connection.invoke('JoinTicketGroup', parseInt(activeTicketId)).catch(e => console.error('Error re-uniendo al grupo:', e));
        });

        await connection.start();
        if (isCancelled) {
          connection.stop().catch(() => { });
          return;
        }

        // Unirse a la sala del ticket
        await connection.invoke('JoinTicketGroup', parseInt(activeTicketId));

        // Escuchar mensajes entrantes en tiempo real
        connection.on('ReceiveMessage', async newMsg => {
          if (!newMsg) return;

          setMessages(prev => {
            // 1. Evitar duplicados si ya existe por idMensaje exacto o por vMediaId
            const exists = prev.some(m => m.idMensaje === newMsg.idMensaje || (m.vMediaId && m.vMediaId === newMsg.vMediaId));
            if (exists) {
              return prev;
            }

            // 2. Si es un mensaje de agente y hay un mensaje temporal optimista pendiente con el mismo contenido, reemplazarlo
            const tempIndex = prev.findIndex(m =>
              (m.isTemp || m.idMensaje > 1000000000000) &&
              (
                (m.vTextoMensaje && newMsg.vTextoMensaje && m.vTextoMensaje.trim() === newMsg.vTextoMensaje.trim()) ||
                (m.vMediaUrl && newMsg.vMediaUrl && m.vMediaUrl === newMsg.vMediaUrl) ||
                (m.vMediaId && newMsg.vMediaId && m.vMediaId === newMsg.vMediaId)
              )
            );

            if (tempIndex !== -1) {
              const updated = [...prev];
              updated[tempIndex] = newMsg;
              return updated;
            }
            return [...prev, newMsg];
          });

          const isFromClient = newMsg.bEsAgente === 0 || newMsg.bEsAgente === false || newMsg.isAgent === false || newMsg.esAgente === false;

          // Si el asesor tiene este ticket abierto en pantalla, mantenerlo como leído
          if (markCurrentAsReadRef.current) {
            markCurrentAsReadRef.current(activeTicketId);
          }

          // Notificar reactivamente a la lista de tickets para actualizar última actividad
          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(activeTicketId),
              bEsAgente: isFromClient ? 0 : 1,
              isAgent: !isFromClient,
              vTextoMensaje: newMsg.vTextoMensaje || newMsg.textoMensaje || newMsg.messageText || '',
              dUltimaActividad: newMsg.dCreatedDate || newMsg.createdDate || new Date().toISOString(),
              isCurrentlyOpen: true
            }
          }));

          // Si el ticket estaba cerrado y un cliente responde (reapertura), refrescamos estado e historial
          const currentStatus = Number(ticketDetailRef.current?.iEstadoTicket ?? propTicket?.iEstadoTicket);
          if (isFromClient && currentStatus === 2) {
            if (fetchTicketDetailRef.current) {
              await fetchTicketDetailRef.current();
            }
            if (onTicketUpdatedRef.current) {
              onTicketUpdatedRef.current();
            }
            if (onRefreshHistoryRef.current) {
              onRefreshHistoryRef.current();
            }
          }
        });
      } catch (err) {
        console.warn('[TicketDetailView] Error en conexión SignalR:', err);
      }
    };

    startSignalR();

    return () => {
      isCancelled = true;
      if (connection) {
        connection.invoke('LeaveTicketGroup', parseInt(activeTicketId)).catch(() => { });
        connection.stop().catch(() => { });
      }
    };
  }, [activeTicketId, token, setMessages]);
};
